/**
 * Loading visual «produção» (engrenagens) — hub e busca de obra.
 *
 * CHECKPOINT v2 (2026-06-02)
 * ─────────────────────────
 * Reverter rápido (sem git): defina `PH_HUB_USE_LEGACY_LOADING_VISUAL = true` abaixo.
 * Reverter completo: `git revert` ou `git checkout` do commit «checkpoint: hub loading produção v2».
 */

/** @type {boolean} true = anéis + texto simples (comportamento anterior) */
export const PH_HUB_USE_LEGACY_LOADING_VISUAL = false;

/** Boot minimal v3: ver `PH_HUB_USE_MINIMAL_BOOT` em hub-loading-shell.js */

const LOADING_DOTS_HTML =
  '<span class="ph-loading-dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span>';

/** @param {string} text */
export function hubProductionCaptionHtml(text) {
  return `${text}${LOADING_DOTS_HTML}`;
}

export const HUB_PROJECT_SEARCH_LOAD_CAPTIONS = [
  'Sincronizando obras',
  'Atualizando códigos PTH e DM',
  'Preparando busca rápida',
];

/** @type {readonly [number, number, number, number, number, number, string, string][]} */
const HUB_PROD_GEAR_SPECS = [
  [34, 26, 12, 17, 12.4, 8, 'ph-hub-prod-gear ph-hub-prod-gear--lg', 'phHubProdGradA'],
  [72, 33, 10, 12.5, 9.1, 5.5, 'ph-hub-prod-gear ph-hub-prod-gear--md', 'phHubProdGradB'],
  [96, 48, 8, 9, 6.6, 3.8, 'ph-hub-prod-gear ph-hub-prod-gear--sm', 'phHubProdGradA'],
];

/** @param {number} cx @param {number} cy @param {number} teeth @param {number} outerR @param {number} rootR */
function hubGearPathD(cx, cy, teeth, outerR, rootR) {
  const step = (Math.PI * 2) / teeth;
  const tipHalf = step * 0.14;
  const valleyHalf = step * 0.18;
  let d = '';

  for (let i = 0; i < teeth; i++) {
    const segStart = i * step - Math.PI / 2;
    const tipCenter = segStart + step * 0.5;
    const valleyCenter = segStart + step;
    const tip1 = tipCenter - tipHalf;
    const tip2 = tipCenter + tipHalf;
    const root1 = valleyCenter - valleyHalf;
    const root2 = valleyCenter + valleyHalf;

    const px = (r, a) => (cx + r * Math.cos(a)).toFixed(2);
    const py = (r, a) => (cy + r * Math.sin(a)).toFixed(2);

    if (i === 0) {
      d += `M${px(outerR, tip1)} ${py(outerR, tip1)}`;
    } else {
      d += ` L${px(outerR, tip1)} ${py(outerR, tip1)}`;
    }
    d += ` L${px(outerR, tip2)} ${py(outerR, tip2)}`;
    d += ` L${px(rootR, root2)} ${py(rootR, root2)}`;
    d += ` L${px(rootR, root1)} ${py(rootR, root1)}`;
  }

  return `${d} Z`;
}

/**
 * @param {typeof HUB_PROD_GEAR_SPECS[number]} spec
 * @param {string} uid
 * @param {{ glow?: boolean }} [opts]
 */
function hubGearGroupMarkup(spec, uid, opts = {}) {
  const [cx, cy, teeth, outerR, rootR, hubR, className, gradKey] = spec;
  const gradId = `${gradKey}${uid}`;
  const hubStroke = gradKey === 'phHubProdGradB' ? '#14b8a6' : '#0f766e';
  const filterAttr = opts.glow ? ` filter="url(#phHubProdGlow${uid})"` : '';
  const hubPinR = (hubR * 0.34).toFixed(2);

  return `<g class="${className}"${filterAttr}>
    <path class="ph-hub-prod-gear-body" d="${hubGearPathD(cx, cy, teeth, outerR, rootR)}" fill="url(#${gradId})" stroke="#115e59" stroke-width="0.55" stroke-linejoin="round"/>
    <circle cx="${cx}" cy="${cy}" r="${hubR}" fill="#ecfdf5" stroke="${hubStroke}" stroke-width="1.15"/>
    <circle cx="${cx}" cy="${cy}" r="${hubPinR}" fill="#5eead4" opacity="0.72"/>
    <circle cx="${(cx - hubR * 0.22).toFixed(2)}" cy="${(cy - hubR * 0.22).toFixed(2)}" r="${(hubR * 0.18).toFixed(2)}" fill="#ffffff" opacity="0.45"/>
  </g>`;
}

/**
 * SVG — três engrenagens entrelaçadas (tema teal do hub).
 * @param {{ compact?: boolean }} [opts]
 */
export function hubProductionGearsSvgMarkup(opts = {}) {
  const compact = Boolean(opts.compact);
  const cls = compact ? 'ph-hub-prod-gears-svg ph-hub-prod-gears-svg--compact' : 'ph-hub-prod-gears-svg';
  const uid = compact ? 'C' : 'H';
  const [lg, md, sm] = HUB_PROD_GEAR_SPECS;

  return `<svg class="${cls}" viewBox="0 0 120 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="phHubProdGradA${uid}" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#99f6e4"/>
      <stop offset="42%" stop-color="#2dd4bf"/>
      <stop offset="100%" stop-color="#0f766e"/>
    </linearGradient>
    <linearGradient id="phHubProdGradB${uid}" x1="100%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#ccfbf1"/>
      <stop offset="48%" stop-color="#14b8a6"/>
      <stop offset="100%" stop-color="#115e59"/>
    </linearGradient>
    <radialGradient id="phHubProdShine${uid}" cx="35%" cy="28%" r="65%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.35"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
    <filter id="phHubProdGlow${uid}" x="-30%" y="-30%" width="160%" height="160%">
      <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" flood-color="#0d9488" flood-opacity="0.45"/>
    </filter>
  </defs>
  <ellipse cx="58" cy="34" rx="46" ry="24" fill="url(#phHubProdShine${uid})" opacity="0.9"/>
  ${hubGearGroupMarkup(lg, uid, { glow: true })}
  ${hubGearGroupMarkup(md, uid)}
  ${hubGearGroupMarkup(sm, uid)}
  <path class="ph-hub-prod-pipeline" d="M8 58h104" stroke="url(#phHubProdGradB${uid})" stroke-width="2" stroke-linecap="round" stroke-dasharray="6 8" opacity="0.55"/>
  <circle class="ph-hub-prod-pipeline-dot ph-hub-prod-pipeline-dot--1" cx="18" cy="58" r="2.5" fill="#14b8a6"/>
  <circle class="ph-hub-prod-pipeline-dot ph-hub-prod-pipeline-dot--2" cx="52" cy="58" r="2.5" fill="#0d9488"/>
  <circle class="ph-hub-prod-pipeline-dot ph-hub-prod-pipeline-dot--3" cx="86" cy="58" r="2.5" fill="#5eead4"/>
</svg>`;
}

/**
 * Hero do painel principal (substitui anéis quando v2 ativo).
 * @param {string} [captionHtml]
 */
export function hubProductionMainHeroMarkup(captionHtml) {
  const cap =
    captionHtml ||
    hubProductionCaptionHtml('Preparando seu workspace');
  return `<div class="ph-hub-prod-hero" aria-hidden="true">
    <div class="ph-hub-prod-gears-wrap">
      ${hubProductionGearsSvgMarkup()}
      <span class="ph-hub-prod-hero-glow" aria-hidden="true"></span>
    </div>
    <div class="ph-hub-load-copy">
      <p class="ph-hub-kicker">SysFactory · portal</p>
      <h1>Painel inicial</h1>
      <p id="phHubLoadCaption" class="ph-hub-load-caption ph-hub-prod-caption">${cap}</p>
    </div>
  </div>`;
}

/** Badge compacto na sidebar skeleton */
export function hubProductionSidebarBadgeMarkup() {
  return `<span class="ph-hub-prod-sidebar-badge" aria-hidden="true">
    ${hubProductionGearsSvgMarkup({ compact: true })}
  </span>`;
}

/**
 * Bloco de loading dentro de «Buscar obra».
 * @param {string} [captionHtml]
 */
export function hubProjectSearchLoaderMarkup(captionHtml) {
  const cap =
    captionHtml ||
    hubProductionCaptionHtml(HUB_PROJECT_SEARCH_LOAD_CAPTIONS[0]);
  return `<div class="ph-hub-project-search-loader-panel" role="status" aria-live="polite" aria-busy="true" aria-label="Carregando lista de obras">
    <div class="ph-hub-project-search-loader-visual">
      ${hubProductionGearsSvgMarkup({ compact: true })}
    </div>
    <p id="phHubProjectSearchLoadCaption" class="ph-hub-project-search-loader-caption">${cap}</p>
  </div>`;
}
